import moment from "moment";
import { calculateFormula } from "services/utils/formulaUtils";
import {
  checkStatutoryFormula,
  formatDay,
  numberOrNaN as num,
  toDisplayFormula,
  toEngineFormula,
  validateVersionDetails,
} from "pages/PayRoll/Shared/statutoryConfigData";

export {
  ACTIVE_STATUS_COLORS,
  VERSION_STATUS_COLORS,
  activeVersion,
  draftFrom,
  formatDay,
  isLocked,
  versionRange,
} from "pages/PayRoll/Shared/statutoryConfigData";

// Payroll > Configuration > Statutory > ESI: option lists, formula
// helpers and checks. The Configuration screen itself runs on the ESI API
// (esiConfigApi.js); the sample versions, codes and reasons below still
// feed Statutory > ESI and the ESI reports until those are wired.

// ---- Options ----

export const MONTHS = moment.months();
// A contribution period is six months from its start month.
export const periodLabel = (startMonth) => {
  const start = Number(startMonth);
  return start >= 1 && start <= 12 ? `${MONTHS[start - 1]} → ${MONTHS[(start + 4) % 12]}` : "–";
};
export const PERIOD_OPTIONS = MONTHS.map((month, index) => ({ value: String(index + 1), label: periodLabel(index + 1) }));
export const JOINER_COVER_OPTIONS = [
  { value: "DOJ", label: "From date of joining" },
  { value: "NEXT_PERIOD", label: "From next period start" },
];
export const WITHIN_CEILING_OPTIONS = [
  { value: "THAT_MONTH", label: "From that month" },
  { value: "NEXT_PERIOD", label: "From next period start" },
];
export const AMOUNT_BASIS_OPTIONS = [
  { value: "RATE", label: "Monthly rate" },
  { value: "EARNED", label: "Earned in month (after LOP)" },
];
export const ROUNDING_OPTIONS = [
  { value: "NONE", label: "No rounding" },
  { value: "NEAREST", label: "Nearest rupee" },
  { value: "UP", label: "Round up" },
  { value: "DOWN", label: "Round down" },
];
export const CONTRIBUTION_TYPES = [
  { value: "EMPLOYEE_DEDUCTION", label: "Employee deduction" },
  { value: "EMPLOYER_COST", label: "Employer cost" },
];
export const optionLabel = (options, value) => options.find((option) => option.value === value)?.label || value || "–";

// ---- Formula codes ----

export const REQUIRED_CALCULATION_CODES = ["ESI_ELIG_WAGE", "ESI_WAGE"];
// Salary components: no suffix = monthly rate, _E = earned in the month.
export const SALARY_CODES = ["BASIC", "DA", "HRA", "SPECIAL", "CONVEYANCE"];
export const FORMULA_FUNCTIONS = ["ROUND", "ROUNDUP", "ROUNDDOWN", "MIN", "MAX", "IF"];

const ROUNDING_FUNCTIONS = { NEAREST: "ROUND", UP: "ROUNDUP", DOWN: "ROUNDDOWN" };
const withRounding = (rounding, inner) => (ROUNDING_FUNCTIONS[rounding] ? `${ROUNDING_FUNCTIONS[rounding]}(${inner})` : inner);

// The formula a contribution component gets from its fields, with or
// without its conditions (C1b: "generated from the fields").
export const contributionCore = (component) => withRounding(component.rounding, `${component.based_on} * ${component.rate_pct}%`);
export const buildContributionFormula = (component) => {
  let formula = contributionCore(component);
  if (component.type === "EMPLOYEE_DEDUCTION" && component.low_wage_enabled) {
    formula = `IF(AVG_DAILY_WAGE <= ${component.low_wage_limit}, 0, ${formula})`;
  }
  return component.covered_only ? `IF(COVERED, ${formula}, 0)` : formula;
};

// What a component actually runs: calculation formulas get the component's
// rounding; contribution formulas are built from the fields unless edited
// as advanced.
export const runFormulaOf = (component) => {
  if (component.kind === "CALCULATION") return withRounding(component.rounding, component.formula);
  return component.advanced ? component.formula : buildContributionFormula(component);
};

// Short formula for the components table (the mockup's
// ROUNDUP(ESI_WAGE × 0.75%)).
export const tableFormulaOf = (component) =>
  toDisplayFormula(component.kind === "CONTRIBUTION" && !component.advanced ? contributionCore(component) : runFormulaOf(component));

export const finalFormulaOf = (component) => toDisplayFormula(runFormulaOf(component));

// Formula plus its conditions in words, for Version History.
export const historyFormulaOf = (component) => {
  if (component.kind !== "CONTRIBUTION" || component.advanced) return toDisplayFormula(runFormulaOf(component));
  const notes = [
    component.covered_only ? "covered only" : null,
    component.type === "EMPLOYEE_DEDUCTION" && component.low_wage_enabled ? `₹0 if avg daily wage ≤ ${component.low_wage_limit}` : null,
  ].filter(Boolean);
  return [tableFormulaOf(component), ...notes].join(" · ");
};

// chips: the company's formula chips from GET config/options/
// ({ salary, pay_input, runtime }). Only what the API returns is offered;
// an empty list (e.g. no pay inputs) stays empty.
export const calculationCodesFor = (components, ownCode, chips) => [
  ...(chips?.salary || []),
  ...(chips?.pay_input || []),
  ...components.filter((component) => component.kind === "CALCULATION" && component.code !== ownCode).map((component) => component.code),
];
// Contribution formulas read the calculation components only (never
// another contribution). The chips offer just those; the check also allows
// the runtime codes (COVERED, AVG_DAILY_WAGE…) the built formula uses.
export const contributionChipCodesFor = (components, ownCode) =>
  components.filter((component) => component.kind === "CALCULATION" && component.code !== ownCode).map((component) => component.code);
export const contributionCodesFor = (components, ownCode, chips) => [
  ...contributionChipCodesFor(components, ownCode),
  ...(chips?.runtime || []),
];

export const checkEsiFormula = (formula, components, own, chips) =>
  checkStatutoryFormula(formula, {
    allowed: own.kind === "CALCULATION" ? calculationCodesFor(components, own.code, chips) : contributionCodesFor(components, own.code, chips),
    components,
    own,
    formulaOf: runFormulaOf,
  });

// ---- Components shipped as editable examples ----

const calc = (code, name, formula, amountBasis, usedFor, description) => ({
  code,
  name,
  kind: "CALCULATION",
  description,
  formula,
  amount_basis: amountBasis,
  rounding: "NONE",
  used_for: usedFor,
  status: "Active",
});

const contrib = (code, name, type, payslipLabel, rate, rounding, lowWage) => ({
  code,
  name,
  kind: "CONTRIBUTION",
  description: "",
  type,
  payslip_label: payslipLabel,
  based_on: "ESI_WAGE",
  rate_pct: rate,
  rounding,
  covered_only: true,
  low_wage_enabled: Boolean(lowWage),
  low_wage_limit: lowWage || "",
  advanced: false,
  formula: "",
  used_for: type === "EMPLOYEE_DEDUCTION" ? "Deduction" : "Employer cost",
  status: "Active",
});

export const defaultComponents = (rounding = "UP") => [
  calc("ESI_ELIG_WAGE", "ESI Eligibility Wage", "BASIC + DA + SPECIAL", "RATE", "Coverage test at period start (monthly rate)", "Wage compared with the ceiling at the start of each contribution period."),
  calc(
    "ESI_WAGE",
    "ESI Contribution Wage",
    "BASIC_E + DA_E + SPECIAL_E + OT + SHIFT",
    "EARNED",
    "Contribution base (earned, after LOP)",
    "Wage base for employee and employer ESI contribution (earned amounts, after LOP).",
  ),
  contrib("EMP_ESI", "Employee ESI", "EMPLOYEE_DEDUCTION", "ESI", "0.75", rounding, "176"),
  contrib("EMPR_ESI", "Employer ESI", "EMPLOYER_COST", "Employer ESI", "3.25", rounding),
];

// ---- Versions ----

const settings = {
  wage_ceiling: "21000",
  disability_ceiling: "25000",
  period1_start_month: "4",
  period2_start_month: "10",
  continue_to_period_end: true,
  joiner_cover_from: "DOJ",
  within_ceiling_cover_from: "NEXT_PERIOD",
};

export const sampleVersions = () => [
  {
    id: 1,
    name: "Version 1",
    effective_from: "2025-04-01",
    effective_to: "2026-03-31",
    status: "Superseded",
    description: "ESI set up for ABC Manufacturing Ltd. Contributions rounded to the nearest rupee.",
    ...settings,
    components: defaultComponents("NEAREST"),
    used_by: ["Apr 2025 – Mar 2026 (12 runs)"],
    created_by: "Paramaguru",
    created_at: "2025-03-14T11:00:00",
  },
  {
    id: 2,
    name: "Version 2",
    effective_from: "2026-04-01",
    effective_to: "",
    status: "Active",
    description: "ESI configuration as per company policy from April 2026. Rounding changed to round up.",
    ...settings,
    components: defaultComponents("UP"),
    used_by: ["Apr 2026 – Sep 2026 (6 runs)"],
    created_by: "Paramaguru",
    created_at: "2026-03-18T16:40:00",
  },
  {
    id: 3,
    name: "Version 3",
    effective_from: "2027-04-01",
    effective_to: "",
    status: "Draft",
    description: "",
    ...settings,
    components: defaultComponents("UP"),
    used_by: [],
    created_by: "",
    created_at: "",
  },
];

// ---- Validation (3.1 table, AC-C01 / C02 / C05) ----

const nextPeriodStart = (startMonth) => String(((Number(startMonth) + 5) % 12) + 1);

export const validateSettings = (form, versions) => {
  const errors = validateVersionDetails(form, versions);
  if (form.wage_ceiling === "" || form.wage_ceiling == null) errors.wage_ceiling = "Enter the ESI wage ceiling";
  else if (!(num(form.wage_ceiling) > 0)) errors.wage_ceiling = "Must be more than 0";
  if (form.disability_ceiling !== "" && form.disability_ceiling != null) {
    if (!(num(form.disability_ceiling) > 0)) errors.disability_ceiling = "Must be more than 0, or leave it blank";
    else if (!errors.wage_ceiling && num(form.disability_ceiling) < num(form.wage_ceiling)) errors.disability_ceiling = "Can't be below the ESI wage ceiling";
  }
  if (!form.period1_start_month) errors.period1_start_month = "Pick Period 1";
  if (!form.period2_start_month) errors.period2_start_month = "Pick Period 2";
  else if (form.period1_start_month && form.period2_start_month !== nextPeriodStart(form.period1_start_month)) {
    errors.period2_start_month = `Must start in ${MONTHS[Number(nextPeriodStart(form.period1_start_month)) - 1]} so the two periods cover 12 months`;
  }
  const active = form.components.filter((component) => component.status === "Active");
  const missing = REQUIRED_CALCULATION_CODES.filter((code) => !active.some((component) => component.code === code));
  const problems = [];
  if (missing.length) problems.push(`Required components must be active: ${missing.join(", ")}`);
  if (!active.some((component) => component.type === "EMPLOYEE_DEDUCTION")) problems.push("Add an active employee deduction component");
  if (!active.some((component) => component.type === "EMPLOYER_COST")) problems.push("Add an active employer cost component");
  if (problems.length) errors.components = problems.join(". ");
  return errors;
};

// ---- Test on employee (sample payroll, Sep 2026) ----

const employee = (id, name, rates, earned, extra = {}) => ({
  id,
  name,
  month: "Sep 2026",
  days_paid: 30,
  covered: true,
  ...Object.fromEntries(SALARY_CODES.map((code) => [code, rates[code] || 0])),
  ...Object.fromEntries(SALARY_CODES.map((code) => [`${code}_E`, (earned || rates)[code] || 0])),
  OT: 0,
  SHIFT: 0,
  INCENTIVE: 0,
  BONUS: 0,
  ...extra,
});

export const TEST_EMPLOYEES = [
  employee("E002", "Anjali Sharma", { BASIC: 11250, DA: 3000, HRA: 4500, SPECIAL: 6750, CONVEYANCE: 1600 }, null, { OT: 1500 }),
  employee("E001", "Ravi Kumar", { BASIC: 9000, DA: 2500, HRA: 3600, SPECIAL: 6500 }),
  employee("E003", "Suresh B", { BASIC: 10000, DA: 2000, HRA: 4000, SPECIAL: 9000 }, { BASIC: 9333, DA: 1867, HRA: 3733, SPECIAL: 8400 }, { days_paid: 28 }),
  employee("E009", "Manish T", { BASIC: 8000, DA: 2000, HRA: 3200, SPECIAL: 6000 }),
  employee("E021", "Selvi M", { BASIC: 3800, DA: 900, SPECIAL: 580 }),
  employee("E022", "Kannan R", { BASIC: 3800, DA: 900, SPECIAL: 610 }),
  employee("E011", "Meera Sharma", { BASIC: 12000, DA: 3000, HRA: 4800, SPECIAL: 7800 }, null, { covered: false }),
];

// Works every active component out for one sample employee, calculation
// components first. AVG_DAILY_WAGE = contribution wage ÷ days paid (2
// decimals); with 0 days the low-wage rule doesn't apply.
export const runComponents = (form, testEmployee, override) => {
  const values = { ...testEmployee, COVERED: testEmployee.covered ? 1 : 0 };
  const ordered = [
    ...form.components.filter((component) => component.kind === "CALCULATION"),
    ...form.components.filter((component) => component.kind === "CONTRIBUTION"),
  ].map((component) => (override && component.code === override.code ? override : component));
  let averaged = false;
  ordered.forEach((component) => {
    if (component.status !== "Active") return;
    if (component.kind === "CONTRIBUTION" && !averaged) {
      averaged = true;
      values.AVG_DAILY_WAGE = testEmployee.days_paid > 0 ? Math.round(((values.ESI_WAGE || 0) / testEmployee.days_paid) * 100) / 100 : Number.MAX_SAFE_INTEGER;
    }
    values[component.code] = Math.max(0, calculateFormula(toEngineFormula(runFormulaOf(component)), values));
  });
  return values;
};

// ---- Employer codes (C4) ----

export const WORK_LOCATIONS = [
  { name: "Erode Plant", employees: 80 },
  { name: "Erode Office", employees: 24 },
  { name: "Chennai Office", employees: 24 },
  { name: "Hosur Warehouse", employees: 0 },
  { name: "Coimbatore Depot", employees: 6 },
];

export const CODE_TYPE_LABELS = { MAIN: "Main code", SUB: "Branch sub-code" };

export const sampleCodes = () => [
  {
    id: 1,
    code: "51000123450001001",
    type: "MAIN",
    branch_name: "ABC Manufacturing Ltd — Head Office",
    coverage_from: "2019-04-01",
    branch_office: "Erode",
    address: "Plot 22, SIPCOT Industrial Park, Perundurai, Erode 638052",
    locations: ["Erode Plant", "Erode Office"],
    status: "Active",
    last_file: "Aug 2026 · v2 · generated 09 Sep 2026",
    last_challan: "Aug 2026 · ₹78,880 · paid 13 Sep 2026",
  },
  {
    id: 2,
    code: "51000123450011001",
    type: "SUB",
    branch_name: "Chennai Branch",
    coverage_from: "2021-06-01",
    branch_office: "Chennai — Ayanavaram",
    address: "4th Floor, Olympia Tech Park, Guindy, Chennai 600032",
    locations: ["Chennai Office"],
    status: "Active",
    last_file: "Sep 2026 · v1 · generated 12 Oct 2026",
    last_challan: "Sep 2026 · ₹18,296 · paid 12 Oct 2026",
  },
  {
    id: 3,
    code: "51000123450021001",
    type: "SUB",
    branch_name: "Hosur Warehouse",
    coverage_from: "2025-01-01",
    branch_office: "Hosur",
    address: "SIPCOT Phase II, Hosur 635109",
    locations: ["Hosur Warehouse"],
    status: "Inactive",
    last_file: "Jun 2025 · v1 · generated 08 Jul 2025",
    last_challan: "Jun 2025 · ₹2,140 · paid 10 Jul 2025",
  },
];

export const employeesAt = (locations) =>
  WORK_LOCATIONS.filter((location) => locations.includes(location.name)).reduce((sum, location) => sum + location.employees, 0);

// Locations with employees that no active code covers (warning on the list,
// Blocking issue in Validation).
export const unlinkedLocations = (codes) => {
  const linked = new Set(codes.filter((code) => code.status === "Active").flatMap((code) => code.locations));
  return WORK_LOCATIONS.filter((location) => location.employees > 0 && !linked.has(location.name));
};

// "51000123450001001" -> "…0001001".
export const shortCode = (code) => (code ? `…${String(code).slice(-7)}` : "—");

export const emptyCode = (codes) => ({
  id: null,
  code: "",
  type: codes.some((code) => code.type === "MAIN") ? "SUB" : "MAIN",
  branch_name: "",
  coverage_from: moment().add(1, "month").startOf("month").format("YYYY-MM-DD"),
  branch_office: "",
  address: "",
  locations: [],
  status: "Active",
  last_file: "",
  last_challan: "",
});

// 3.3 / AC-C09: 17 digits, unique, one main code, a location in one active
// code at a time.
export const validateCode = (form, codes) => {
  const errors = {};
  const code = String(form.code || "").trim();
  if (!code) errors.code = "Enter the code exactly as on the ESIC portal";
  else if (!/^\d{17}$/.test(code)) errors.code = "17 digits, e.g. 51000123450031001";
  else if (codes.some((item) => item.id !== form.id && item.code === code)) errors.code = "This code is already added";
  if (form.type === "MAIN" && codes.some((item) => item.id !== form.id && item.type === "MAIN")) errors.type = "A main code is already added";
  if (form.type === "SUB" && !codes.some((item) => item.type === "MAIN")) errors.type = "Add the main code first";
  if (!String(form.branch_name || "").trim()) errors.branch_name = "Enter the branch name";
  if (!form.coverage_from) errors.coverage_from = "Pick the coverage date";
  if (!form.locations.length) errors.locations = "Pick at least one work location";
  else if (form.status === "Active") {
    const clash = form.locations
      .map((location) => ({ location, other: codes.find((item) => item.id !== form.id && item.status === "Active" && item.locations.includes(location)) }))
      .find((item) => item.other);
    if (clash) errors.locations = `${clash.location} is already linked to ${clash.other.code}. A location can belong to one code at a time.`;
  }
  return errors;
};

// ---- Reason codes (C5) ----

// ESIC reason-code list: Cauver product metadata, read-only for companies.
export const REASON_LIST = { verified_on: "2026-10-01", list_version: "2026.1" };
export const ESIC_REASON_CODES = [
  { code: 0, label: "Without reason", lwd: false, removes: false },
  { code: 1, label: "On leave", lwd: false, removes: false },
  { code: 2, label: "Left service", lwd: true, removes: true },
  { code: 3, label: "Retired", lwd: true, removes: true },
  { code: 4, label: "Out of coverage", lwd: true, removes: true },
  { code: 5, label: "Expired", lwd: true, removes: true },
  { code: 6, label: "Non-implemented area", lwd: true, removes: true },
  { code: 7, label: "Compliance by immediate employer", lwd: false, removes: false },
  { code: 8, label: "Suspension of work", lwd: false, removes: false },
  { code: 9, label: "Strike / lockout", lwd: false, removes: false },
  { code: 10, label: "Retrenchment", lwd: true, removes: true },
  { code: 11, label: "No work", lwd: false, removes: false },
];

// The company's reasons (People › Exit reasons and absence types) plus the
// one the system sets itself.
export const COMPANY_REASONS = [
  { name: "Resignation", source: "Exit reason" },
  { name: "Termination", source: "Exit reason" },
  { name: "Absconding", source: "Exit reason" },
  { name: "Contract ended", source: "Exit reason" },
  { name: "Retirement", source: "Exit reason" },
  { name: "Superannuation", source: "Exit reason" },
  { name: "Death", source: "Exit reason" },
  { name: "Retrenchment", source: "Exit reason" },
  { name: "Plant closure", source: "Exit reason" },
  { name: "Absent all month (unauthorised)", source: "Absence type" },
  { name: "On unpaid leave all month", source: "Absence type" },
  { name: "Suspended all month", source: "Absence type" },
  { name: "Lay-off / no work all month", source: "Absence type" },
  { name: "Wages above ceiling at new period (system)", source: "System" },
];

export const sampleReasonMapping = () => ({
  0: ["Absent all month (unauthorised)"],
  1: ["On unpaid leave all month"],
  2: ["Resignation", "Termination", "Absconding"],
  3: ["Retirement", "Superannuation"],
  4: ["Wages above ceiling at new period (system)"],
  5: ["Death"],
  8: ["Suspended all month"],
  10: ["Retrenchment", "Plant closure"],
  11: ["Lay-off / no work all month"],
});

export const RECENT_REASON_USE = {
  1: { file: "Sep 2026 ESIC file", employees: ["Latha M"] },
  2: { file: "Sep 2026 ESIC file", employees: ["Karthik S", "Mani K"] },
};

export const reasonCodeOf = (mapping, reason) => {
  const found = Object.keys(mapping).find((code) => (mapping[code] || []).includes(reason));
  return found == null ? null : Number(found);
};

export const unmappedReasons = (mapping) => COMPANY_REASONS.filter((reason) => reasonCodeOf(mapping, reason.name) == null);

export const reasonListLine = () => `ESIC code list maintained by Cauver · last verified on the ESIC portal ${formatDay(REASON_LIST.verified_on)}`;

